/**
 * Le pointage de la garderie sur le téléphone de l'animateur : ce que la page
 * d'un lien sait sans la base.
 *
 * Un lien vaut pour une école et une année scolaire (migration
 * `20261031200000_le_pointage_de_la_garderie`). La page lit la liste d'un jour
 * par `garderie_lien_lire`, pointe par `garderie_lien_pointer`, et c'est la
 * base qui décide de tout ce qui compte : le lien est-il vivant, l'enfant
 * est-il de cette école, le mois est-il facturé. Ici : la forme des données,
 * les plages du jour, la plage par défaut, les groupes, la recherche, les
 * dates, les textes.
 */
import { jourDeLaSemaine } from './calcul';
import { attenteDuRefus, messageDAttente } from '../liens-codes';

export interface PlageDuLien {
  id: string;
  code: string;
  libelle: string;
  /** `HH:MM`. */
  heureDebut: string;
  heureFin: string;
  /** 1 = lundi … 5 = vendredi. */
  jours: number[];
}

export type Groupe = 'maternelle' | 'primaire';
export type FiltreDeGroupe = 'tous' | Groupe;

export interface EnfantDuLien {
  id: string;
  prenom: string;
  nom: string;
  groupe: Groupe | null;
  classe: string | null;
  /** Les plages où il est pointé ce jour-là (identifiants). */
  presences: string[];
  /**
   * Ses habitudes : par plage (identifiant), le nombre de jours où il y est venu dans les
   * 8 semaines avant le lundi de la semaine lue (migration `20261102180000`) ; `{}` si la base
   * ne les donne pas.
   */
  habitudes: Record<string, number>;
}

export interface LecturePointage {
  codeRequis: false;
  ecole: { id: string; nom: string; code: string };
  anneeScolaire: number;
  /** `AAAA-MM-JJ`. */
  date: string;
  aujourdHui: boolean;
  ferie: string | null;
  moisFacture: boolean;
  /** Le code de la plage que l'heure suggère, ou `null`. */
  plageProposee: string | null;
  plages: PlageDuLien[];
  enfants: EnfantDuLien[];
}

export type LectureDuLien = { codeRequis: true } | LecturePointage;

export interface ParentDeLaFiche {
  prenom: string | null;
  nom: string;
  telephone: string | null;
  reference: boolean;
  partPct: number;
}

export interface FicheGarderie {
  codeRequis: false;
  enfant: {
    id: string;
    prenom: string;
    nom: string;
    dateNaissance: string | null;
    classe: string | null;
    groupe: Groupe | null;
    rentreLeMidi: boolean;
    formule: string | null;
  };
  parents: ParentDeLaFiche[];
  depart: {
    partSeul: boolean | null;
    heure: string | null;
    personnes: { prenom: string | null; nom: string | null; telephone: string | null; lien: string | null }[];
  } | null;
  sante: {
    allergies: boolean | null;
    allergiesDetails: string | null;
    allergiesConsequences: string | null;
    regime: boolean | null;
    regimeDetails: string | null;
    medicaments: boolean | null;
    medicamentsDetails: string | null;
    medicamentsAutonomie: boolean | null;
    aSavoir: string | null;
    medecinNom: string | null;
    medecinTelephone: string | null;
  } | null;
}

export type LectureDeFiche = { codeRequis: true } | FicheGarderie;

export type ResultatDuPointage = { codeRequis: true } | { codeRequis: false; present: boolean };

/**
 * D'où vient la liste et où va le geste : le lien de l'animateur (sans compte,
 * jeton et code ; `pointage.supabase.ts`) ou Gestion (un compte de
 * l'administration ou de la coordination ; `presences-bureau.supabase.ts`).
 * L'écran est le même ; la base juge chaque geste dans les deux cas.
 */
export interface SourceDuPointage {
  /** La liste d'une date ; `{ codeRequis: true }` quand le lien demande son code. */
  lire(date: string): Promise<LectureDuLien>;
  /** Un ✓ (`present`) ou un ✗ ; l'état après le geste, tel que la base le dit. */
  pointer(enfantId: string, date: string, plageId: string, present: boolean): Promise<ResultatDuPointage>;
  /** La fiche de l'enfant (parents, départ, santé), quand la source la donne. */
  lireFiche?: (enfantId: string) => Promise<LectureDeFiche>;
  /** Ce sous quoi le téléphone garde le dernier groupe choisi. */
  cle: string;
}

/* ------------------------------------------------------------------------- */
/* Lecture des réponses                                                       */
/* ------------------------------------------------------------------------- */

type J = Record<string, unknown>;
const o = (v: unknown): J => (v && typeof v === 'object' && !Array.isArray(v) ? (v as J) : {});
const s = (v: unknown): string | null => (typeof v === 'string' ? v : null);
const b = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
const n = (v: unknown): number => (typeof v === 'number' ? v : Number(v ?? 0) || 0);
const tableau = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const groupe = (v: unknown): Groupe | null => (v === 'maternelle' || v === 'primaire' ? v : null);
/** `{ "<plage_id>": jours }` → des nombres entiers positifs ; le reste est ignoré. */
export const lireHabitudes = (v: unknown): Record<string, number> =>
  Object.fromEntries(
    Object.entries(o(v))
      .map(([k, x]) => [k, n(x)] as const)
      .filter(([, x]) => Number.isFinite(x) && x > 0),
  );

export function lireLecture(data: unknown): LectureDuLien {
  const d = o(data);
  if (d.code_requis === true) return { codeRequis: true };
  const ecole = o(d.ecole);
  return {
    codeRequis: false,
    ecole: { id: s(ecole.id) ?? '', nom: s(ecole.nom) ?? '', code: s(ecole.code) ?? '' },
    anneeScolaire: n(d.annee_scolaire),
    date: s(d.date) ?? '',
    aujourdHui: d.aujourd_hui === true,
    ferie: s(d.ferie),
    moisFacture: d.mois_facture === true,
    plageProposee: s(d.plage_proposee),
    plages: tableau(d.plages).map((p) => {
      const x = o(p);
      return {
        id: s(x.id) ?? '',
        code: s(x.code) ?? '',
        libelle: s(x.libelle) ?? '',
        heureDebut: s(x.heure_debut) ?? '',
        heureFin: s(x.heure_fin) ?? '',
        jours: tableau(x.jours).map(n),
      };
    }),
    enfants: tableau(d.enfants).map((e) => {
      const x = o(e);
      return {
        id: s(x.id) ?? '',
        prenom: s(x.prenom) ?? '',
        nom: s(x.nom) ?? '',
        groupe: groupe(x.groupe),
        classe: s(x.classe),
        presences: tableau(x.presences).map((p) => String(p)),
        habitudes: lireHabitudes(x.habitudes),
      };
    }),
  };
}

export function lireFiche(data: unknown): LectureDeFiche {
  const d = o(data);
  if (d.code_requis === true) return { codeRequis: true };
  const e = o(d.enfant);
  const dep = d.depart && typeof d.depart === 'object' ? o(d.depart) : null;
  const sa = d.sante && typeof d.sante === 'object' ? o(d.sante) : null;
  return {
    codeRequis: false,
    enfant: {
      id: s(e.id) ?? '',
      prenom: s(e.prenom) ?? '',
      nom: s(e.nom) ?? '',
      dateNaissance: s(e.date_naissance),
      classe: s(e.classe),
      groupe: groupe(e.groupe),
      rentreLeMidi: e.rentre_le_midi === true,
      formule: s(e.formule),
    },
    parents: tableau(d.parents).map((p) => {
      const x = o(p);
      return { prenom: s(x.prenom), nom: s(x.nom) ?? '', telephone: s(x.telephone), reference: x.reference === true, partPct: n(x.part_pct) };
    }),
    depart: dep
      ? {
          partSeul: b(dep.part_seul),
          heure: s(dep.heure),
          personnes: tableau(dep.personnes).map((p) => {
            const x = o(p);
            return { prenom: s(x.prenom), nom: s(x.nom), telephone: s(x.telephone), lien: s(x.lien) };
          }),
        }
      : null,
    sante: sa
      ? {
          allergies: b(sa.allergies),
          allergiesDetails: s(sa.allergies_details),
          allergiesConsequences: s(sa.allergies_consequences),
          regime: b(sa.regime),
          regimeDetails: s(sa.regime_details),
          medicaments: b(sa.medicaments),
          medicamentsDetails: s(sa.medicaments_details),
          medicamentsAutonomie: b(sa.medicaments_autonomie),
          aSavoir: s(sa.a_savoir),
          medecinNom: s(sa.medecin_nom),
          medecinTelephone: s(sa.medecin_telephone),
        }
      : null,
  };
}

/* ------------------------------------------------------------------------- */
/* Les plages, les groupes, la recherche                                      */
/* ------------------------------------------------------------------------- */

/** Les plages qui tournent ce jour-là (le mercredi, l'après-midi ; le week-end, rien). */
export function plagesDuJour(plages: readonly PlageDuLien[], date: string): PlageDuLien[] {
  const jour = jourDeLaSemaine(date);
  return plages.filter((p) => p.jours.includes(jour));
}

/**
 * La plage qu'ouvre l'heure (demande du 02/11/2026 : « matin si on est le matin,
 * ATL si l'après-midi ») : celle qui tourne maintenant (la dernière commencée, ATL2
 * à 16 h et non ATL1) ; sinon, le matin, la plage du matin ; l'après-midi, la
 * prochaine à commencer, ou la dernière de la journée quand elle est finie. Une
 * école sans plage du matin (la CSSA) ouvre sa plage de l'après-midi. Les heures
 * sont des `HH:MM`.
 */
export function plageDeLHeure<T extends { heureDebut: string; heureFin: string }>(duJour: readonly T[], heure: string): T | null {
  if (duJour.length === 0) return null;
  const h = heure.slice(0, 5);
  const tries = [...duJour].sort((a, b) => a.heureDebut.localeCompare(b.heureDebut));
  const enCours = tries.filter((p) => p.heureDebut.slice(0, 5) <= h && h < p.heureFin.slice(0, 5));
  if (enCours.length > 0) return enCours[enCours.length - 1];
  const matin = h < '12:00';
  const duCote = tries.filter((p) => (p.heureDebut.slice(0, 5) < '12:00') === matin);
  if (duCote.length === 0) return matin ? tries[0] : tries[tries.length - 1];
  if (matin) {
    const commencees = duCote.filter((p) => p.heureDebut.slice(0, 5) <= h);
    return commencees.length > 0 ? commencees[commencees.length - 1] : duCote[0];
  }
  return duCote.find((p) => p.heureDebut.slice(0, 5) > h) ?? duCote[duCote.length - 1];
}

/**
 * La plage à ouvrir : celle que l'animateur a choisie si elle tourne ce jour-là ;
 * aujourd'hui, celle de l'heure (`plageDeLHeure`) ; sinon celle que la base
 * propose, sinon la première du jour. L'écran l'appelle une fois, à l'ouverture
 * d'une date, puis la garde : la liste ne change pas de plage toute seule quand
 * l'horloge passe 16 h.
 */
export function plageParDefaut(lecture: LecturePointage, duJour: readonly PlageDuLien[], choisie: string | null, heure?: string): string | null {
  if (choisie && duJour.some((p) => p.id === choisie)) return choisie;
  if (lecture.aujourdHui && heure) {
    const deLHeure = plageDeLHeure(duJour, heure);
    if (deLHeure) return deLHeure.id;
  }
  const proposee = duJour.find((p) => p.code === lecture.plageProposee);
  if (proposee) return proposee.id;
  return duJour[0]?.id ?? null;
}

const sansAccents = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** La valeur du filtre de classe qui garde les enfants sans classe connue. */
export const SANS_CLASSE = '∅';

/**
 * Le filtre : un groupe, une classe (`''` : toutes ; `SANS_CLASSE` : les enfants sans classe),
 * et une recherche par début de prénom ou de nom.
 */
export function filtrerEnfants(enfants: readonly EnfantDuLien[], groupe: FiltreDeGroupe, recherche: string, classe = ''): EnfantDuLien[] {
  const q = sansAccents(recherche.trim());
  return enfants.filter((e) => {
    if (groupe !== 'tous' && e.groupe !== groupe) return false;
    if (classe === SANS_CLASSE ? e.classe !== null : classe !== '' && e.classe !== classe) return false;
    if (!q) return true;
    return sansAccents(e.prenom).startsWith(q) || sansAccents(e.nom).startsWith(q) || sansAccents(`${e.prenom} ${e.nom}`).startsWith(q);
  });
}

const alphabet = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

/**
 * L'ordre de la liste (demande du 02/11/2026) : d'abord les enfants qui viennent le plus
 * souvent sur la plage ouverte — le matin, les habitués du matin —, puis l'ordre alphabétique
 * (nom, puis prénom). Les habitudes sont comptées avant le lundi de la semaine lue (le même ordre
 * sur le téléphone et dans Gestion) : cocher ne déplace personne.
 * Sans plage, l'ordre alphabétique seul.
 */
export function trierParHabitude<T extends { id: string; nom: string; prenom: string; habitudes: Record<string, number> }>(
  enfants: readonly T[],
  plageId: string | null,
): T[] {
  const jours = (e: T) => (plageId ? (e.habitudes[plageId] ?? 0) : 0);
  return [...enfants].sort(
    (a, b) => jours(b) - jours(a) || alphabet.compare(a.nom, b.nom) || alphabet.compare(a.prenom, b.prenom) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/**
 * Une relecture (toutes les 15 s) rafraîchit les ✓, jamais l'ordre : pour la même école et la
 * même date, chaque enfant garde les habitudes de la première lecture. Sans cela, un jour passé
 * réencodé ailleurs (un collègue, Gestion) déplacerait des lignes sous le doigt de l'animateur.
 * Un enfant nouveau dans la liste garde les siennes ; une autre date ou une autre école repart
 * de la lecture reçue.
 */
export function garderLesHabitudes(avant: LecturePointage | null, apres: LecturePointage): LecturePointage {
  if (!avant || avant.date !== apres.date || avant.ecole.id !== apres.ecole.id) return apres;
  const connues = new Map(avant.enfants.map((e) => [e.id, e.habitudes]));
  return {
    ...apres,
    enfants: apres.enfants.map((e) => {
      const h = connues.get(e.id);
      return h && h !== e.habitudes ? { ...e, habitudes: h } : e;
    }),
  };
}

const ordreDesClasses = new Intl.Collator('fr', { sensitivity: 'base', numeric: true });

/**
 * Les classes à proposer dans le filtre, pour le groupe choisi : chacune avec son nombre
 * d'enfants, dans l'ordre naturel (M1 avant M2, P2 avant P10) ; et le nombre d'enfants sans
 * classe connue.
 */
export function classesDeLaListe(enfants: readonly EnfantDuLien[], groupe: FiltreDeGroupe): { classes: { classe: string; nombre: number }[]; sansClasse: number } {
  const parClasse = new Map<string, number>();
  let sansClasse = 0;
  for (const e of enfants) {
    if (groupe !== 'tous' && e.groupe !== groupe) continue;
    if (e.classe === null) sansClasse += 1;
    else parClasse.set(e.classe, (parClasse.get(e.classe) ?? 0) + 1);
  }
  return {
    classes: [...parClasse.entries()].map(([classe, nombre]) => ({ classe, nombre })).sort((a, b) => ordreDesClasses.compare(a.classe, b.classe)),
    sansClasse,
  };
}

/** Le nombre d'enfants pointés sur une plage, dans la liste donnée. */
export function compterPresents(enfants: readonly EnfantDuLien[], plageId: string | null): number {
  if (!plageId) return 0;
  return enfants.filter((e) => e.presences.includes(plageId)).length;
}

/** Le nombre d'enfants par groupe. */
export function compterParGroupe(enfants: readonly EnfantDuLien[]): Record<FiltreDeGroupe, number> {
  return {
    tous: enfants.length,
    maternelle: enfants.filter((e) => e.groupe === 'maternelle').length,
    primaire: enfants.filter((e) => e.groupe === 'primaire').length,
  };
}

/** La liste après un geste : la présence ajoutée ou retirée, sans toucher au reste. */
export function appliquerPointage(enfants: readonly EnfantDuLien[], enfantId: string, plageId: string, present: boolean): EnfantDuLien[] {
  return enfants.map((e) => {
    if (e.id !== enfantId) return e;
    const sans = e.presences.filter((p) => p !== plageId);
    return { ...e, presences: present ? [...sans, plageId] : sans };
  });
}

/* ------------------------------------------------------------------------- */
/* Les dates                                                                  */
/* ------------------------------------------------------------------------- */

/** `AAAA-MM-JJ` à Bruxelles, maintenant. */
export function aujourdHuiBruxelles(maintenant: Date = new Date()): string {
  return new Intl.DateTimeFormat('fr-CA', { timeZone: 'Europe/Brussels', year: 'numeric', month: '2-digit', day: '2-digit' }).format(maintenant);
}

/** `HH:MM` à Bruxelles, maintenant (24 h). */
export function heureBruxelles(maintenant: Date = new Date()): string {
  const parties = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Brussels', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).formatToParts(maintenant);
  const valeur = (type: string) => parties.find((p) => p.type === type)?.value ?? '00';
  return `${valeur('hour').padStart(2, '0')}:${valeur('minute').padStart(2, '0')}`;
}

const decaler = (date: string, jours: number): string => {
  const [a, m, j] = date.split('-').map(Number);
  const d = new Date(Date.UTC(a, m - 1, j + jours));
  return d.toISOString().slice(0, 10);
};
export const veille = (date: string): string => decaler(date, -1);
export const lendemain = (date: string): string => decaler(date, 1);

const JOURS = ['', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
const MOIS = ['', 'janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

/** « lundi 5 octobre 2026 ». */
export function dateEnClair(date: string): string {
  const [a, m, j] = date.split('-').map(Number);
  if (!a || !m || !j) return date;
  return `${JOURS[jourDeLaSemaine(date)]} ${j === 1 ? '1er' : j} ${MOIS[m]} ${a}`;
}

/** L'âge en années révolues, ou `null`. */
export function ageEnAnnees(dateNaissance: string | null, aujourdHui: string): number | null {
  if (!dateNaissance) return null;
  const [an, mn, jn] = dateNaissance.split('-').map(Number);
  const [a, m, j] = aujourdHui.split('-').map(Number);
  if (!an || !a) return null;
  let age = a - an;
  if (m < mn || (m === mn && j < jn)) age -= 1;
  return age;
}

/* ------------------------------------------------------------------------- */
/* Les textes                                                                 */
/* ------------------------------------------------------------------------- */

export function initiales(prenom: string, nom: string): string {
  return `${prenom.trim().charAt(0)}${nom.trim().charAt(0)}`.toUpperCase() || '?';
}

/** Ce que la base refuse, en français. */
export function messageDeRefus(message: string): string {
  const attente = attenteDuRefus(message);
  if (attente !== null) return messageDAttente(attente);
  if (message.startsWith('[garderie-mois-facture]')) return 'Ce mois est déjà facturé : la présence ne peut plus changer. Signalez-la à la coordination.';
  switch (message) {
    case 'lien_inconnu':
      return 'Ce lien n’existe pas. Demandez le bon lien à la coordination.';
    case 'lien_revoque':
      return 'Ce lien a été remplacé. Demandez le nouveau lien à la coordination.';
    case 'lien_expire':
      return 'Ce lien n’est plus valable : l’année scolaire est finie.';
    case 'lien_bloque':
      return 'Ce lien est bloqué après trop de codes faux. La coordination doit en créer un autre.';
    case 'date_future':
      return 'On ne pointe pas une date à venir.';
    case 'date_hors_annee':
      return 'Cette date est hors de l’année scolaire.';
    case 'enfant_hors_lien':
      return 'Cet enfant n’est pas de cette école.';
    case 'plage_hors_lien':
      return 'Cette plage n’existe pas pour cette école.';
    case 'rien_a_changer':
      return 'Rien à enregistrer.';
    default:
      return message || 'Quelque chose n’a pas marché. Réessayez.';
  }
}

export function adresseDuLien(origine: string, jeton: string): string {
  return `${origine.replace(/\/$/, '')}/garderie/${jeton}`;
}

/** Le message à coller dans le groupe WhatsApp de l'école. */
export function messagePourLesAnims(args: { ecole: string; adresse: string; code: string | null }): string {
  const lignes = [
    `Pointage garderie ${args.ecole} :`,
    args.adresse,
    '',
    'Ouvrez ce lien sur votre téléphone, choisissez la plage (matin, ATL, mercredi) et cochez les enfants présents. Chaque ✓ s’enregistre tout seul ; décocher retire la présence.',
  ];
  if (args.code) lignes.push('', `Code : ${args.code} (à garder pour vous).`);
  return lignes.join('\n');
}
